import * as React from 'react'
import { DriverContractDetailsRow } from './DriverContractDetailsRow'

import AddTemplate from '../../../../../../../assets/icons_new/AddTemplate.svg'
import AddContractWhite from '../../../../../../../assets/icons_new/AddContractWhite.svg'

import DriverContractActions from './DriverContractActions'
import TableTitle from '@/modules/drivers/shared/tableItems/TableTitle'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { useTable } from '@/modules/drivers/context/TableContext'
import useDriverService from '@/modules/drivers/services/DriverService'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { getDriverContractsDetailsColumns } from '@/modules/drivers/constants/tables'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { ContractFormProvider } from '@/modules/drivers/context/ContractFormContext'
import ContractModal from '@/modules/drivers/modals/contract/ContractModal'
import TableTitileActionButtons from '@/modules/drivers/shared/tableItems/TableTitileActionButton'
import { useModals } from '@/modules/drivers/stores/modalsStore'
import ModalsContainer from '@/modules/drivers/shared/actionBars/details/ModalsContainer'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import CreateTemplateModal from '@/modules/drivers/modals/contract/template/CreateTemplateModal'
import AddContractByTemplate from '@/modules/drivers/modals/contract/template/AddContractByTemplate'

const DriverContractsTable = ({ driverId, companyId, driverName }) => {
  const { t } = useLocalize()
  const columns = getDriverContractsDetailsColumns(t)
  const { toggleAll, toggleId, selectedGroup, isSelectedAny } = useTable()
  const {
    setIsCreateContractOpen,
    setIsAnnexOpen,
    isOpenEditContractModal,
    setIsOpenEditContractModal,
    isEditAnnexOpen,
    setIsEditAnnexOpen,
    setIsOpenCreateTemplate,
    isOpenCreateTemplate,
    setIsOpenContractFromTemplate,
    isOpenContractFromTemplate,
    isOpenContractWithDataFromTemplate,
    setIsOpenContractWithDataFromTemplate,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedContractIdFromTemplate,
    setSelectedContractIdFromTemplate,
  } = useModals()

  const { refreshState } = useDrivers()
  const { getDriverContracts } = useDriverService()
  const [lastAnnexDate, setLastAnnexDate] = React.useState<string | undefined>(
    undefined,
  )

  const [contracts, setContracts] = React.useState([])
  const [baseContractId, setBaseContractId] = React.useState<
    number | undefined
  >(undefined)
  const { sortedData, sortConfig, handleHeaderClick, setSortConfig } =
    useSortableData({
      data: contracts,
      tableColumns: columns,
    })

  const lastAnnex = React.useMemo(() => {
    if (!contracts?.length) return undefined

    return contracts
      .filter((c) => c.isAnnex)
      .sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      )[0]
  }, [contracts])

  // Znacznik "trwa" w tabeli - zawsze najnowszy aneks.
  const actualAnnexId = lastAnnex?.id
  // Aneks do edycji bierzemy z zaznaczenia, nie z lastAnnex. Wczesniej modal zawsze
  // otwieral i zapisywal najnowszy aneks, wiec przy dwoch i wiecej aneksach starszych
  // nie dalo sie w ogole poprawic (np. zdjac PIT-2 za miesiac, ktory obejmuje starszy aneks).
  const editedAnnex = React.useMemo(() => {
    const selectedId = selectedGroup?.ids?.[0]
    if (selectedId == null) return lastAnnex

    return (
      contracts.find(
        (c) => c.isAnnex && Number(c.id) === Number(selectedId),
      ) ?? lastAnnex
    )
  }, [contracts, selectedGroup, lastAnnex])

  const editedAnnexId = editedAnnex?.id
  const editedAnnexParentId =
    editedAnnex?.parentContractId ?? baseContractId?.toString()

  React.useEffect(() => {
    setLastAnnexDate(lastAnnex?.startDate)
  }, [lastAnnex])
  const fetchData = async () => {
    const contracts = await getDriverContracts({
      companyId: companyId,
      driverId: Number(driverId),
    })
    setContracts(contracts)

    const baseContract = contracts.find((c) => c.isAnnex === false)
    if (baseContract) {
      setBaseContractId(baseContract.id)
    }
  }
  React.useEffect(() => {
    if (companyId && driverId) {
      fetchData()
    }
  }, [companyId, driverId, refreshState])
  React.useEffect(() => {
    setSortConfig({ key: 'startDate', direction: 'desc' })
  }, [contracts])

  return (
    <div className="tw-pb-4">
      {isSelectedAny ? (
        <DriverContractActions
          setIsOpenCreateTemplate={setIsOpenCreateTemplate}
          companyId={companyId}
          setIsOpenAnnex={(e) => setIsAnnexOpen(e)}
          driverId={driverId}
          setIsOpenEdit={(e) => {
            selectedGroup.additionalType === 'ANNEX'
              ? setIsEditAnnexOpen(e)
              : setIsOpenEditContractModal(e)
          }}
        />
      ) : (
        <div className="tw-flex tw-items-center tw-justify-between">
          <TableTitle title={t('contract.contract')} />
          <div className="tw-flex tw-gap-2">
            <TableTitileActionButtons
              actions={[
                // @TODO add edit templates action
                //   {
                //     alt: t('contract.edit-templates'),
                //     icon: EditTemplate,
                //     onClick: () => setIsOpenContractFromTemplate(true),
                //     tooltip: t('contract.edit-templates'),
                //   },
                {
                  alt: t('contract.add-by-template'),
                  icon: AddTemplate,
                  onClick: () => setIsOpenContractFromTemplate(true),
                  tooltip: t('contract.add-by-template'),
                },
                {
                  alt: t('contract.add-contract'),
                  icon: AddContractWhite,
                  onClick: () => setIsCreateContractOpen(true),
                  tooltip: t('contract.add-contract'),
                  primary: true,
                },
              ]}
            />
          </div>
        </div>
      )}
      <div
        className={`tw-overflow-hidden tw-rounded-lg tw-border tw-border-x-base-300 tw-border-t-base-300 ${isSelectedAny && 'tw-rounded-t-none'}`}
      >
        <div className='tw-w-full tw-overflow-x-auto'>
          <table className="tw-w-full tw-border-collapse tw-table-auto !tw-rounded-3xl">
            <TableHeader
              columns={columns}
              fontSize="md"
              isSelectedAny={isSelectedAny}
              rounded
              sortConfig={sortConfig}
              bolder
              disabledToggle
              toggleAll={toggleAll}
              onHeaderClick={handleHeaderClick}
            />
            <tbody className="">
              {sortedData &&
                sortedData.map((contract, i) => (
                  <DriverContractDetailsRow
                    key={contract.id}
                    contract={contract}
                    actualAnnexId={actualAnnexId}
                    selectedIds={selectedGroup?.ids ?? []}
                    toggleId={toggleId}
                    onDoubleClick={() => {
                      if (!selectedGroup?.ids?.includes(Number(contract.id))) {
                        toggleId(
                          Number(contract.id),
                          true,
                          contract?.isAnnex ? 'ANNEX' : 'CONTRACT',
                        )
                      }
                      if (contract.isAnnex) {
                        setIsEditAnnexOpen(true)
                      } else {
                        setIsOpenEditContractModal(true)
                      }
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {isOpenEditContractModal && (
        <ContractFormProvider
          companyId={companyId}
          contractId={selectedGroup?.ids[0]?.toString()}
          isEdit={isOpenEditContractModal ?? false}
        >
          <ContractModal
            driversId={driverId}
            companyId={companyId}
            driverNames={[driverName]}
            contractId={selectedGroup?.ids[0]?.toString()}
            close={() => {
              setIsOpenEditContractModal(false)
            }}
          />
        </ContractFormProvider>
      )}
      {isOpenContractWithDataFromTemplate && selectedTemplateId && (
        <ContractFormProvider
          companyId={companyId}
          driverId={driverId}
          contractId={selectedContractIdFromTemplate}
          isFromTemplate={true}
          templateId={selectedTemplateId}
        >
          <ContractModal
            driversId={driverId}
            companyId={companyId}
            driverNames={[driverName]}
            contractId={selectedGroup?.ids[0]?.toString()}
            close={() => {
              setIsOpenContractWithDataFromTemplate(false)
            }}
          />
        </ContractFormProvider>
      )}
      {isEditAnnexOpen && (
        <ContractFormProvider
          companyId={companyId}
          contractId={editedAnnexParentId}
          isAnnex={true}
          isEdit={isEditAnnexOpen ?? false}
          annexId={editedAnnexId?.toString()}
          lastAnnexDate={lastAnnexDate}
        >
          <ContractModal
            annexInitialStartDate={editedAnnex?.startDate}
            driversId={driverId}
            anmexId={editedAnnexId?.toString()}
            companyId={companyId}
            driverNames={[driverName]}
            contractId={editedAnnexParentId}
            close={() => {
              setIsEditAnnexOpen(false)
            }}
          />
        </ContractFormProvider>
      )}
      {isOpenCreateTemplate && (
        <CreateTemplateModal
          companyId={companyId}
          driverId={driverId}
          contractId={selectedGroup?.ids[0]?.toString()}
          close={() => setIsOpenCreateTemplate(false)}
        />
      )}
      {isOpenContractFromTemplate && (
        <AddContractByTemplate
          setSelectedContractIdFromTemplate={setSelectedContractIdFromTemplate}
          close={() => setIsOpenContractFromTemplate(false)}
          companyId={companyId}
          setSelectedTemplateId={setSelectedTemplateId}
          setIsOpenContractWithDataFromTemplate={
            setIsOpenContractWithDataFromTemplate
          }
        />
      )}
      <ModalsContainer
        selectedContractId={selectedGroup?.ids[0]?.toString()}
        lastAnnexDate={lastAnnexDate}
        driverId={driverId}
        contractId={baseContractId?.toString()}
        companyId={companyId}
        selectedDriverNames={[driverName]}
      />
    </div>
  )
}

export default DriverContractsTable
